import booleanIntersects from "@turf/boolean-intersects";
import { bbox, bboxPolygon, centroid, circle, distance, featureCollection, point, rhumbDestination, sector, transformRotate, transformScale, transformTranslate } from "@turf/turf";
import mapConfig from "../config/map.config";
import { layers } from "../config/map.constants";
import { smokePlumeImg } from "../config/map.constants";
import { getJson, saveJson } from "./offline/cache.service";
import { _requestJson } from "./request.service";


/**
 * 
 * @param {string} url 
 * @param {lng:number, lat:number} lnglat 
 * @param {number} radius 
 * @param {boolean} returnGeometry 
 * @param {string[]} outFields 
 * @returns 
 */
export async function getFeaturesByLonLat(url, lnglat, radius, returnGeometry, outFields = ['*']) {
	returnGeometry = returnGeometry ? returnGeometry : false;
	radius = radius ? radius : mapConfig.smokePlumeRadius;

	const params = new URLSearchParams({
		inSR: 4326,
		geometryType: 'esriGeometryPoint',
		geometry: `${lnglat.lng},${lnglat.lat}`,
		returnGeometry,
		//       time: this._time,
		outFields: outFields.join(','),
		spatialRel: 'esriSpatialRelEnvelopeIntersects',
		units: 'esriSRUnit_Meter',
		distance: radius,
		f: 'geojson'
	})

	// _appendTokenIfExists(params)

	return new Promise((resolve, reject) => {
		_requestJson(`${url}/query?${params.toString()}`, null)
			.then(data => {
				const features = data && data.features ? data.features : null;
				resolve(features)
			}).catch(reject)
	})
}

/**
 * 
 * @param {[lat,long]} lnglat 
 * @param {*} geojson 
 * @returns 
 */
export function queryGeoJsonFeatureByLonLat(lnglat, geojson) {
	const pt = point([lnglat[1],lnglat[0]]);
	return geojson.features.find(f => booleanIntersects(pt, f));
}

/**
 * 
 * @param {string} url 
 * @param {xmin:number, ymin:number, xmax:number, ymax:number} bounds 
 * @param {boolean} returnGeometry 
 * @param {string[]} outFields 
 * @returns 
 */
export async function getFeaturesByBounds(url, bounds, returnGeometry, outFields = ['*']) {
	returnGeometry = returnGeometry ? returnGeometry : false
	const params = new URLSearchParams({
		inSR: 4326,
		// geometryType: 'esriGeometryPoint',
		geometryType: 'esriGeometryEnvelope',
		geometry: `{xmin: ${bounds.xmin}, ymin: ${bounds.ymin}, xmax: ${bounds.xmax}, ymax: ${bounds.ymax}}`,
		returnGeometry,
		outFields: outFields.join(','),
		spatialRel: 'esriSpatialRelEnvelopeIntersects',
		units: 'esriSRUnit_Meter',
		f: 'geojson'
	})

	// _appendTokenIfExists(params)

	return new Promise((resolve, reject) => {
		_requestJson(`${url}/query?${params.toString()}`, null)
			.then(data => {
				const features = data && data.features ? data.features : null;
				resolve(features);
			}).catch(reject)
	})
}

/**
 * 
 * @param {string} url 
 * @param {boolean} returnGeometry 
 * @param {string[]} outFields 
 * @returns 
 */
export async function getAllFeatures(url, returnGeometry, outFields = ['*']) {
	returnGeometry = returnGeometry ? returnGeometry : false
	const params = new URLSearchParams({
		inSR: 4326,
		geometryType: 'esriGeometryEnvelope',
		returnGeometry,
		outFields: outFields.join(','),
		spatialRel: 'esriSpatialRelEnvelopeIntersects',
		units: 'esriSRUnit_Meter',
		where: '1=1',
		f: 'geojson'
	})

	// _appendTokenIfExists(params)

	return new Promise((resolve, reject) => {
		_requestJson(`${url}/query?${params.toString()}`, null)
			.then(data => {
				const features = data && data.features ? data.features : null;
				resolve(features);
			}).catch(reject)
	})
}


/**
 * Fetch all features for layer from map server and cache locally.
 * @param {string} layerId 
 */
export const fetchAndCacheLayerFeatures = async (layerId) => {
	const layerInfo = layers[layerId];
	if (!layerInfo) return;
	try {
		const features = await getAllFeatures(layerInfo.url, true, layerInfo.arcGisOpts.outFields);
		cacheLayerFeatures(layerId, features);
	} catch(e) {
		console.log("COULD NOT FETCH: ",e)
	}
}

/**
 * @param {string} layerId 
 * @returns all cached layer's features
 */
export const getCachedLayerFeatures = (layerId) => {
	return getJson(`${layerId}_layer_features`)
}

export const cacheLayerFeatures = (layerId, features) => {
	// saveJson(`${layerId}_layer_features`, features);
	saveJson(`${layerId}_layer_features`, {
		type: "FeatureCollection",
		features,
		layerId
	})
}

function _appendTokenIfExists(params) {
	params.append('token', mapConfig.esriApiKey)
}

/**
 * Returns a rectangle (turf.js polygon) used as the bounds for the smoke plume image.
 * @param {[[number,number],[number,number]]} bounds latLong bounds of the map
 * @param {number} figureWidth Desired rectangle width in miles
 * @param {number} rotateBy Amount in degrees to rotate the image so that it's right arrow points right (0 degrees)
 * @param {number} translateBy Amount to translate image by (x axis only)
 */
export const getSmokePlumeRectangle = (bounds, figureWidth, rotateBy, translateBy) => {
	let plumeImageBbox = bboxPolygon(bounds);

	const pivot = centroid(plumeImageBbox);
	const extentWidth = distance(point([plumeImageBbox.bbox[0],0]),  point([plumeImageBbox.bbox[2],0]), {units: 'miles'});
	const diagramStemWidth = figureWidth * translateBy;
	const scaleBy = (figureWidth + diagramStemWidth)/extentWidth;

	plumeImageBbox = transformScale(plumeImageBbox, scaleBy, {mutate: false, centroid: pivot});
	const realCenter = figureWidth/2 - diagramStemWidth

	// Reset image rotation (point the arrow to the right, 0 degrees) and center it on center of map.
	plumeImageBbox = transformRotate(plumeImageBbox, rotateBy, {pivot});
	plumeImageBbox = transformTranslate(plumeImageBbox, realCenter, 93, {units: 'miles'});

	return plumeImageBbox;
}

/**
 * Returns a turf.js semi-circle representing the area covered by the smoke plume
 * @param {number} center center of plume
 * @param {number} radius radius (length) of smoke plume
 * @returns Feature 
 */
export const getSmallSmokePlumePolygon = (center, radius) => {
	const arcAngle = smokePlumeImg.small.arcAngle;
	const cone = sector(center, radius, -arcAngle + 90, arcAngle + 90, {units: 'miles'});
	return cone;
}

/**
 * Returns a turf.js semi-circle representing the area covered by the smoke plume
 * @param {number} center center of plume
 * @param {number} radius radius (length) of smoke plume
 * @returns Feature
 */
export const getLargeSmokePlumePolygon = (center, radius) => {
	const arcAngle = smokePlumeImg.large.arcAngle;
	const distToCircleOrigin = 1.5;
	radius = radius + distToCircleOrigin

	const circleOrigin = rhumbDestination(center, -distToCircleOrigin, 90, {units: 'miles'});
	const cone1 = sector(circleOrigin, radius, 90 -arcAngle*2, arcAngle*2 + 90, {units: 'miles'});
	return cone1
}

/**
 * Returns a turf.js square representing the planned burns square area. 
 * @param {number} center center of square
 * @returns Feature
 */
export const getPlannedBurnsAreaPolygon = (center) => {
	// 6x6 square miles = 3 mile radius
	return bboxPolygon(bbox(circle(center, 3, { steps: 64, units: 'miles' })))
}
